import { AdminTeams } from '@platform/client';
import { AdminTeamMember, NewMember } from '@platform/client/schemas';
import {
  RemovePendingNewMemberButton,
  AddMemberInputRow,
  RemoveExistingMemberButton,
  UndoRemoveMemberButton,
} from './ModalFormButtons';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { AdminTableContext } from '../../../AdminTable';
import { AdminFormModal, ModalStage } from '../../../AdminFormModal';
import dataTestids from 'apps/web/src/app/constants/test-ids';

export const EditMembersModal = ({
  teamId,
  cohortId,
  onClose,
  incomingNewMembers,
}: {
  teamId: number;
  cohortId: number;
  onClose: () => void;
  incomingNewMembers?: AdminTeamMember[];
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const [newMembers, setNewMembers] = useState<(NewMember | AdminTeamMember)[] | undefined>(
    incomingNewMembers,
  );
  const [removeMembers, setRemoveMembers] = useState<(AdminTeamMember | NewMember)[]>();
  const [addNewMemberId, setAddNewMemberId] = useState<number>();
  const { data: membersData } = AdminTeams.useGetTeamMembers({ team_id: teamId });
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const { data } = AdminTeams.useGetNewMember(
    {
      user_id: addNewMemberId ?? 0, // should be fine, only fetched when button clicked
    },
    {
      query: {
        enabled: !!addNewMemberId,
      },
    },
  );
  const closeModal = () => {
    setNewMembers(undefined);
    setRemoveMembers(undefined);
    setAddNewMemberId(undefined);
    onClose();
  };
  const queryClient = useQueryClient();
  const updateMemberMutator = AdminTeams.useUpdateTeamMembers({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamsQueryKey({
            cohort_id: cohortId,
            page: adminQueryOptionsV2.page,
            limit: adminQueryOptionsV2.limit,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetFreeAgentsQueryKey({
            cohort_id: cohortId,
            page: adminQueryOptionsV2.page,
            limit: adminQueryOptionsV2.limit,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamsCountsQueryKey({
            cohort_id: cohortId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamIdsQueryKey({ cohort_id: cohortId }),
        });
        queryClient.invalidateQueries({
          queryKey: AdminTeams.getGetTeamDetailsQueryKey({ team_id: teamId }),
        });
        setModalStage('success');
      },
      onError: () => {
        setModalStage('error');
      },
      onMutate: () => {
        setModalStage('submitting');
      },
    },
  });
  useEffect(() => {
    if (data && addNewMemberId) {
      // we check for addNewmember because data may be cached
      setNewMembers((prev) => {
        // prevent duplicates
        if (membersData?.members.find((member) => member.id === data.member.id)) {
          return prev;
        }
        if (prev) {
          if (prev.find((member) => member.id === data.member.id)) {
            return prev;
          }
          return [...prev, data.member];
        } else {
          return [data.member];
        }
      });
    }
  }, [data, setNewMembers, addNewMemberId, membersData]);
  return (
    <AdminFormModal
      modalStage={modalStage}
      variant='dialog'
      onClose={closeModal}
      title='Edit Team Members'
      submitCTA={{
        text: 'Save changes',
        callback: () => {
          const memberIds = [
            ...(newMembers?.map((member) => member.id) ?? []),
            ...(membersData?.members
              .filter((member) => !removeMembers?.find((rm) => rm.id === member.id))
              .map((member) => member.id) ?? []),
          ];
          updateMemberMutator.mutate({
            data: {
              teamId: teamId,
              memberIds: memberIds,
            },
          });
        },
        isDisabled: () =>
          (!newMembers || newMembers.length === 0) &&
          (!removeMembers || removeMembers.length === 0),
      }}
    >
      <div className='w-full'>
        {membersData?.members
          ?.filter((member) => !removeMembers?.find((rm) => rm.id === member.id))
          .map((member) => (
            <div
              key={`current-${member.id}`}
              className='flex justify-between'
              data-testid={`${dataTestids.ADMIN_TEAMS.MEMBER_EDIT_MODAL.CURRENT_MEMBER}-${member.id}`}
            >
              {member.name} (ID: {member.id})
              <RemoveExistingMemberButton teamMember={member} setRemoveMembers={setRemoveMembers} />
            </div>
          ))}
        {newMembers?.map((member) => (
          <div
            key={`new-${member.id}`}
            className='flex justify-between text-success-500'
            data-testid={`${dataTestids.ADMIN_TEAMS.MEMBER_EDIT_MODAL.NEW_MEMBER}-${member.id}`}
          >
            {member.name} (ID: {member.id})
            <RemovePendingNewMemberButton
              pendingMemberId={member.id}
              setNewMembers={setNewMembers}
            />
          </div>
        ))}
        {removeMembers && removeMembers.length > 0 && (
          <div className='text-error-500'>
            {removeMembers.map((member) => (
              <div
                key={`remove-${member.id}`}
                className='flex justify-between line-through'
                data-testid={`${dataTestids.ADMIN_TEAMS.MEMBER_EDIT_MODAL.DELETED_MEMBER}-${member.id}`}
              >
                {member.name} (ID: {member.id})
                <UndoRemoveMemberButton
                  removedMemberId={member.id}
                  setRemoveMembers={setRemoveMembers}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <AddMemberInputRow setAddNewMemberId={setAddNewMemberId} />
    </AdminFormModal>
  );
};
